import Vue from 'vue';
import VueRouter from 'vue-router';
import { dataInserted } from 'framework/router/data-inserted-hook';
import { createLazyWrapper, prepareRecord } from 'framework/lazy-route';
import templates from 'framework/lazy-route/page-templates';

// NOTE: Disable browser implemented `scrollRestoration` as we take care of this ourselves
if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
}


/**
 * Initialize vue-router
 * @param {Vue.Component} root - The app's root component
 */
function init(root) {
    const Log = Vue.log({ context: { location: 'vue-router' } });
    const unsync = Vue.$fetchRoute.connect(root.store); // eslint-disable-line no-unused-vars
    const routes = Vue.$fetchRoute.decorateRecords(globals.config.router.routes, [prepareRecord]);
    const initialRoute = (window.location.pathname + window.location.search);

    // NOTE: This route is manually added to make the frontend styleguide accessible without a backend dependency
    if (window.styleguide) {
        routes.push({
            path: '*',
            component: createLazyWrapper(templates.styleguide),
        });
    } else if (!routes.length) {
        Log.warn('Serving a demo page as there were no registered routes!');
        routes.push({
            path: '*',
            component: createLazyWrapper(templates.sample),
        });
    }

    const router = new VueRouter({
        mode: 'history',
        routes,
        linkActiveClass: 'u-router-link-active',
        linkExactActiveClass: 'u-router-link-exact-active',
        scrollBehavior(from, to, savedPosition) {
            let result = savedPosition || { x: 0, y: 0 };

            return new Promise(resolve => {
                const unregister = dataInserted(() => {
                    Log.debug(`Route transition - Updating scroll state: ${ JSON.stringify(result) }`);

                    // NOTE: This will work in a future release post `vue-router@3.0.1`
                    // @link https://github.com/vuejs/vue-router/commit/c93a734dd40fbc76752930b27e2aa2ce39a5097e
                    /*
                    // Check if any matched route config has meta that requires scrolling to top
                    if (to.matched.some(m => m.meta.scrollToTop)) {
                        result = { x: 0, y: 0 };
                    }

                    // Wait for the out transition to complete (if necessary)
                    this.app.$root.$once('root:trigger-scroll', () => {
                        // If the resolved position is falsy or an empty object, it will retain current scroll position
                        resolve(result);
                    });
                    */

                    if (to.hash && document.getElementById(to.hash)) {
                        result = { selector: to.hash };
                    }

                    resolve(result);
                    unregister();
                });
            });
        },
    });


    // Call hook before every route transition
    router.beforeEach((to, from, next) => {
        Log.debug('beforeEach hook');
        const hasMatches = (to.matched && to.matched.length);
        const areRecordsIdentical = Vue.$fetchRoute.compareRecords(to, from);
        const isInitialRoute = (initialRoute === to.fullPath);

        // Simple link without corresponding route record
        if (!hasMatches && !isInitialRoute) {
            Log.debug('No matches found, following path url');
            window.location.href = to.path;
            return;
        }

        // djangoCMS edit mode
        if (!isInitialRoute && globals.config.cmsEditMode && !areRecordsIdentical) {
            Log.debug('djangoCMS edit mode, following path url');
            window.location.href = to.path;
            return;
        }

        // Registered route record
        Log.debug('Following matched route');
        next();
    });

    // Assign props to the root component
    root.router = router;
}

export default init;
